import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronDown, ChevronRight, Info } from "lucide-react";

// ============================================================
//  Erklärungen gehören nicht in den Fließtext eines Werkzeugs.
//  Sichtbar bleibt: Zahl, Label, höchstens ein kurzer Satz. Alles Weitere
//  liegt hinter einem Info-Symbol (Popover) oder einem Aufklapp-Element.
// ============================================================

/** Info-Symbol mit Popover. Öffnet per Klick, schließt bei Klick außerhalb oder Escape. */
export function InfoTip({ children, label = "Erklärung", size = 14 }: { children: ReactNode; label?: string; size?: number }) {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  // Am rechten Rand nach links aufklappen, sonst läuft das Popover aus dem Viewport.
  const toggle = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setAlignRight(r.left + 360 > window.innerWidth);
    setOpen((o) => !o);
  };
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown); document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  return (
    <span ref={ref} className="relative inline-flex align-middle">
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={toggle}
        className="text-faint hover:text-strong inline-flex items-center justify-center rounded-full"
        style={{ width: size + 8, height: size + 8, backgroundColor: open ? "var(--surface-sunken)" : "transparent" }}
      >
        <Info size={size} />
      </button>
      {open && (
        <span
          role="dialog"
          className={`text-dim absolute top-full z-30 mt-1 block w-[22rem] max-w-[80vw] rounded-lg p-3 text-xs leading-relaxed shadow-lg ${alignRight ? "right-0" : "left-0"}`}
          style={{ backgroundColor: "var(--surface-card)", borderWidth: 1, borderColor: "var(--border-strong)" }}
        >
          {children}
        </span>
      )}
    </span>
  );
}

/** Aufklappbarer Block — geschlossen ist nur die Zeile mit dem Label sichtbar. */
export function Aufklapp({ label, children, defaultOpen = false, className = "" }: { label: ReactNode; children: ReactNode; defaultOpen?: boolean; className?: string }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={className}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="text-dim hover:text-strong inline-flex items-center gap-1.5 text-xs font-medium">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {label}
      </button>
      {open && <div className="text-dim mt-2 text-xs leading-relaxed">{children}</div>}
    </div>
  );
}

/** Label mit Info-Symbol daneben — für Kartentitel und Spaltenköpfe. */
export function LabelMitInfo({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1">
      {label}
      <InfoTip>{children}</InfoTip>
    </span>
  );
}
